<!--
  RecordField — record-reference renderer (`_record`, `parent_id`, …); the
  chrome-free inner GridCell hosts. Display-only: an archive icon (when the
  target is archived) + the target record's name as a link + a hover expand
  button (emits `expand` to open the record in place), empty when there's no
  target. Renders the same in every `mode`. Reference-kind, so `modelValue`
  is the entity array; the record is `modelValue[0]`.
-->
<template>
  <div v-if="record" class="record-field g-2" :class="sizeClass">
    <span
      v-if="record.archived_at"
      class="icon is-small text-muted shrink-0 archived-icon"
      title="Archived"
    >
      <font-awesome-icon icon="archive" />
    </span>
    <AugerLink :target="recordTarget(record)" class="font-semibold text-primary is-elided">
      {{ record.name }}
    </AugerLink>
    <button class="btn expand-record" @click.stop="emit('expand', record)">
      <span class="icon">
        <font-awesome-icon icon="up-right-and-down-left-from-center" size="xs" />
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import type { Field } from "@auger/core/fields"
import AugerLink from "../../components/link/AugerLink.vue"
import { recordTarget } from "../../components/link/registry"
import { type FieldMode, type FieldSize, textSizeClass } from "../useField"

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    field: Field
    mode: FieldMode
    size?: FieldSize
  }>(),
  { size: "small" }
)

// update:modelValue/commit/cancel are declared for contract uniformity but never
// fired (display-only); the expand button opens the record in place.
const emit = defineEmits<{
  "update:modelValue": [value: unknown]
  commit: [payload?: { advance?: boolean }]
  cancel: []
  expand: [record: unknown]
}>()

const sizeClass = computed(() => textSizeClass(props.size))

// GridCell passes the reference entities; the record is the first (and only)
// entry. Guard for an empty / orphaned reference.
const record = computed(() => {
  const entities = props.modelValue as Array<Record<string, any>> | null | undefined
  return Array.isArray(entities) ? (entities[0] ?? null) : null
})
</script>

<style>
.record-field {
  display: flex;
  align-items: center;
  align-self: stretch;
  width: 100%;

  .expand-record {
    height: 20px;
    margin-left: auto;
    min-width: 22px;
    float: right;
    right: 4px;
    padding: 0;
    border: 1px solid var(--auger-border-light);
    opacity: 0;
    visibility: hidden;
  }

  &:hover .expand-record {
    opacity: 1;
    transition: opacity 0.8s ease;
    visibility: inherit;
  }

  /* In a tall row GridCell top-aligns this shell (so a wrapped name starts on
     the first line), which aligns the icon to the text's line box rather than
     its glyphs — the line box is taller, so a top-aligned icon reads as
     sitting above center. Center it against its line-height sibling instead. */
  .archived-icon {
    align-self: center;
  }
}

/* Tall rows have the vertical room for the whole name, so let the link wrap —
   `.is-elided`'s single ellipsised line is the short-row behavior. */
.grid-element:not(.is-short-rows) .record-field .is-elided {
  white-space: normal;
  overflow-wrap: break-word;
  text-overflow: clip;
}
</style>
